import React, { useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, FormGroup, FormText, Input, Label, Modal, ModalBody, ModalHeader } from "reactstrap";
import AlertComponent from "../../../_UI/Components/Alerts/AlertComponent";
import { LoadingSpinnerButton } from "../../../_UI/Components/PathwayButtons";
import { adminAreaActions } from "../../services/store/adminarea.actions";

export const ReportBugModal = ({ isModalOpen, bugReportLoading, bugReportResult, bugReportError, organizationUUID, dispatch, milestones, milestonesLoading, projectLabels, projectLabelsLoading }) => {
    const [bugTitle, setBugTitle] = useState("");
    const [bugDescription, setBugDescription] = useState("");
    const [expectedBehavior, setExpectedBehavior] = useState("");
    const [stepsToReproduce, setStepsToReproduce] = useState("");
    const [selectedMilestoneId, setSelectedMilestoneId] = useState("");
    const [selectedLabels, setSelectedLabels] = useState([]);
    const [labelSearch, setLabelSearch] = useState("");
    const [labelDropdownOpen, setLabelDropdownOpen] = useState(false);
    const [screenshots, setScreenshots] = useState([]);
    const [isDragging, setIsDragging] = useState(false);
    const [useGitlabTemplate, setUseGitlabTemplate] = useState(false);
    const [templateContent, setTemplateContent] = useState("");
    const labelDropdownRef = useRef(null);
    const fileInputRef = useRef(null);
    const dropZoneRef = useRef(null);

    useEffect(() => {
        if (!isModalOpen || useGitlabTemplate) return;
        if (!milestones?.length) {
            dispatch(adminAreaActions.getActiveMilestones());
        }
        if (!projectLabels?.length) {
            dispatch(adminAreaActions.getProjectLabels());
        }
    }, [isModalOpen, useGitlabTemplate, dispatch, milestones, projectLabels]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (labelDropdownRef.current && !labelDropdownRef.current.contains(e.target)) {
                setLabelDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const filteredLabels = useMemo(() => {
        if (!projectLabels) return [];
        if (!labelSearch.trim()) return projectLabels;
        const search = labelSearch.toLowerCase();
        return projectLabels.filter((l) => l.name.toLowerCase().includes(search));
    }, [projectLabels, labelSearch]);

    const toggleLabel = (labelName) => {
        setSelectedLabels((prev) => {
            if (prev.includes(labelName)) {
                return prev.filter((l) => l !== labelName);
            }
            setLabelSearch("");
            return [...prev, labelName];
        });
    };

    const getLabelObj = (name) => projectLabels?.find((l) => l.name === name);

    const addImageFiles = (files) => {
        const imageFiles = files.filter((f) => f.type.startsWith("image/"));
        if (imageFiles.length > 0) {
            setScreenshots((prev) => [...prev, ...imageFiles]);
        }
    };

    const handleScreenshotAdd = (e) => {
        addImageFiles(Array.from(e.target.files || []));
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(true);
    };

    const handleDragLeave = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (dropZoneRef.current && !dropZoneRef.current.contains(e.relatedTarget)) {
            setIsDragging(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragging(false);
        addImageFiles(Array.from(e.dataTransfer.files || []));
    };

    const handlePaste = (e) => {
        const items = Array.from(e.clipboardData?.items || []);
        const imageFiles = items
            .filter((item) => item.type.startsWith("image/"))
            .map((item) => item.getAsFile())
            .filter(Boolean);
        if (imageFiles.length > 0) {
            e.preventDefault();
            addImageFiles(imageFiles);
        }
    };

    const removeScreenshot = (index) => {
        setScreenshots((prev) => prev.filter((_, i) => i !== index));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!bugTitle.trim()) return;

        const requestData = {
            organizationUUID,
            bugTitle: bugTitle.trim(),
            bugDescription: useGitlabTemplate ? null : bugDescription.trim() || null,
            expectedBehavior: useGitlabTemplate ? null : expectedBehavior.trim() || null,
            stepsToReproduce: useGitlabTemplate ? null : stepsToReproduce.trim() || null,
            currentUrl: window.location.href,
            milestoneId: useGitlabTemplate || !selectedMilestoneId ? null : parseInt(selectedMilestoneId, 10),
            labels: useGitlabTemplate || selectedLabels.length === 0 ? null : selectedLabels,
            useGitlabTemplate,
            templateContent: useGitlabTemplate ? templateContent.trim() || null : null,
        };

        const formData = new FormData();
        formData.append("request", new Blob([JSON.stringify(requestData)], { type: "application/json" }));
        screenshots.forEach((file) => {
            formData.append("screenshots", file);
        });

        dispatch(adminAreaActions.createBugReport(formData));
    };

    const handleClose = () => {
        setBugTitle("");
        setBugDescription("");
        setExpectedBehavior("");
        setStepsToReproduce("");
        setSelectedMilestoneId("");
        setSelectedLabels([]);
        setLabelSearch("");
        setLabelDropdownOpen(false);
        setScreenshots([]);
        setIsDragging(false);
        setUseGitlabTemplate(true);
        setTemplateContent("");
        dispatch(adminAreaActions.toggleBugReportModal());
    };

    return (
        <Modal
            className="item-added-modal"
            isOpen={isModalOpen}
            toggle={bugReportLoading ? undefined : handleClose}
            backdrop={bugReportLoading ? "static" : true}
            keyboard={!bugReportLoading}
            size="lg"
        >
            <ModalHeader toggle={bugReportLoading ? undefined : handleClose}>Bug melden</ModalHeader>
            <ModalBody>
                {!bugReportResult && (
                    <Form onSubmit={handleSubmit}>
                        <FormGroup>
                            <Label for="bugTitle">Titel *</Label>
                            <Input
                                id="bugTitle"
                                name="bugTitle"
                                placeholder="Bug-Titel eingeben"
                                type="text"
                                value={bugTitle}
                                onChange={(e) => setBugTitle(e.target.value)}
                                required
                            />
                        </FormGroup>
                        <FormGroup check style={{ marginBottom: "1rem" }}>
                            <Label check>
                                <Input
                                    type="checkbox"
                                    checked={useGitlabTemplate}
                                    onChange={(e) => setUseGitlabTemplate(e.target.checked)}
                                />{" "}
                                Gem{"ä"}{"ß"} GitLab-Vorlage verwenden
                            </Label>
                            <FormText color="muted">
                                Wenn aktiviert, werden Beschreibung, Expected Behavior, Steps to Reproduce und Labels {"ü"}ber die GitLab-Vorlage und Quick Actions gesetzt.
                            </FormText>
                        </FormGroup>
                        {useGitlabTemplate && (
                            <FormGroup>
                                <Label for="templateContent">GitLab Issue-Inhalt (Vorlage)</Label>
                                <Input
                                    id="templateContent"
                                    name="templateContent"
                                    type="textarea"
                                    rows={16}
                                    style={{ fontFamily: "monospace", fontSize: "13px" }}
                                    value={templateContent}
                                    onChange={(e) => setTemplateContent(e.target.value)}
                                    placeholder={`# Bug Report AI Template\n\n## Bug Summary\n...\n\n## Labels & Assignments\n/label ~Bug\n/label ~"Status::Backlog"\n/milestone %"1.9.29"\n/assign @marcel.bauer`}
                                />
                                <FormText color="muted">
                                    Den ausgef{"ü"}llten Inhalt der GitLab-Vorlage hier einf{"ü"}gen. Quick Actions wie <code>/label</code>, <code>/milestone</code> oder <code>/assign</code> werden von GitLab automatisch ausgef{"ü"}hrt.
                                </FormText>
                            </FormGroup>
                        )}
                        {!useGitlabTemplate && (
                        <>
                        <FormGroup>
                            <Label for="bugDescription">Beschreibung</Label>
                            <Input
                                id="bugDescription"
                                name="bugDescription"
                                placeholder="Optionale Beschreibung"
                                type="textarea"
                                rows={4}
                                value={bugDescription}
                                onChange={(e) => setBugDescription(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="expectedBehavior">Expected Behavior</Label>
                            <Input
                                id="expectedBehavior"
                                name="expectedBehavior"
                                placeholder={`Was w\u00E4re das erwartete Verhalten?`}
                                type="textarea"
                                rows={3}
                                value={expectedBehavior}
                                onChange={(e) => setExpectedBehavior(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label for="stepsToReproduce">Steps to Reproduce</Label>
                            <Input
                                id="stepsToReproduce"
                                name="stepsToReproduce"
                                placeholder="Schritte zum Reproduzieren des Bugs"
                                type="textarea"
                                rows={3}
                                value={stepsToReproduce}
                                onChange={(e) => setStepsToReproduce(e.target.value)}
                            />
                        </FormGroup>
                        </>
                        )}
                        <FormGroup>
                            <Label>Screenshots</Label>
                            <div
                                ref={dropZoneRef}
                                onDragOver={handleDragOver}
                                onDragLeave={handleDragLeave}
                                onDrop={handleDrop}
                                onPaste={handlePaste}
                                onClick={() => fileInputRef.current?.click()}
                                style={{
                                    border: isDragging ? "2px dashed #007bff" : "2px dashed #ced4da",
                                    borderRadius: "6px",
                                    padding: "20px",
                                    textAlign: "center",
                                    cursor: "pointer",
                                    backgroundColor: isDragging ? "#e8f0fe" : "#fafafa",
                                    transition: "all 0.2s ease",
                                }}
                            >
                                <input
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={handleScreenshotAdd}
                                    ref={fileInputRef}
                                    style={{ display: "none" }}
                                />
                                <i className="fa fa-cloud-upload" style={{ fontSize: "24px", color: "#999", marginBottom: "6px" }} />
                                <div style={{ color: "#666", fontSize: "13px" }}>
                                    {isDragging
                                        ? "Dateien hier ablegen"
                                        : "Bilder hierher ziehen, klicken oder einf\u00FCgen (Strg+V)"}
                                </div>
                            </div>
                            {screenshots.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginTop: "8px" }}>
                                    {screenshots.map((file, index) => (
                                        <div
                                            key={index}
                                            style={{
                                                position: "relative",
                                                width: "80px",
                                                height: "80px",
                                                border: "1px solid #ced4da",
                                                borderRadius: "4px",
                                                overflow: "hidden",
                                            }}
                                        >
                                            <img
                                                src={URL.createObjectURL(file)}
                                                alt={file.name}
                                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => removeScreenshot(index)}
                                                style={{
                                                    position: "absolute",
                                                    top: "2px",
                                                    right: "2px",
                                                    background: "rgba(0,0,0,0.6)",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "50%",
                                                    width: "20px",
                                                    height: "20px",
                                                    fontSize: "12px",
                                                    cursor: "pointer",
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",
                                                    padding: 0,
                                                }}
                                                title="Entfernen"
                                            >
                                                {"\u00D7"}
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </FormGroup>
                        {!useGitlabTemplate && (
                        <>
                        <FormGroup>
                            <Label for="bugMilestone">
                                Milestone{" "}
                                <button
                                    type="button"
                                    onClick={() => dispatch(adminAreaActions.getActiveMilestones())}
                                    style={{ border: "none", background: "none", cursor: "pointer", padding: "0 4px" }}
                                    title="Milestones aktualisieren"
                                    disabled={milestonesLoading}
                                >
                                    <i className={`fa fa-refresh${milestonesLoading ? " fa-spin" : ""}`} style={{ fontSize: "12px", color: "#999" }} />
                                </button>
                            </Label>
                            <Input
                                id="bugMilestone"
                                name="bugMilestone"
                                type="select"
                                value={selectedMilestoneId}
                                onChange={(e) => setSelectedMilestoneId(e.target.value)}
                                disabled={milestonesLoading}
                            >
                                <option value="">
                                    {milestonesLoading ? "Laden..." : "Kein Milestone"}
                                </option>
                                {milestones &&
                                    milestones.map((milestone) => (
                                        <option key={milestone.id} value={milestone.id}>
                                            {milestone.title}
                                        </option>
                                    ))}
                            </Input>
                        </FormGroup>
                        </>
                        )}
                        <FormGroup>
                            <Label>
                                Labels{" "}
                                <button
                                    type="button"
                                    onClick={() => dispatch(adminAreaActions.getProjectLabels())}
                                    style={{ border: "none", background: "none", cursor: "pointer", padding: "0 4px" }}
                                    title="Labels aktualisieren"
                                    disabled={projectLabelsLoading}
                                >
                                    <i className={`fa fa-refresh${projectLabelsLoading ? " fa-spin" : ""}`} style={{ fontSize: "12px", color: "#999" }} />
                                </button>
                            </Label>
                            {selectedLabels.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: "4px", marginBottom: "8px" }}>
                                    {selectedLabels.map((name) => {
                                        const label = getLabelObj(name);
                                        return (
                                            <span
                                                key={name}
                                                style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    padding: "2px 8px",
                                                    borderRadius: "12px",
                                                    fontSize: "12px",
                                                    fontWeight: 500,
                                                    backgroundColor: label?.color || "#e0e0e0",
                                                    color: label?.textColor || "#333",
                                                    cursor: "pointer",
                                                }}
                                                onClick={() => toggleLabel(name)}
                                                title="Klicken zum Entfernen"
                                            >
                                                {name} <span style={{ marginLeft: "4px", fontWeight: "bold" }}>{"\u00D7"}</span>
                                            </span>
                                        );
                                    })}
                                </div>
                            )}
                            <div ref={labelDropdownRef} style={{ position: "relative" }}>
                                <Input
                                    type="text"
                                    placeholder={projectLabelsLoading ? "Laden..." : "Labels suchen..."}
                                    value={labelSearch}
                                    onChange={(e) => {
                                        setLabelSearch(e.target.value);
                                        setLabelDropdownOpen(true);
                                    }}
                                    onFocus={() => setLabelDropdownOpen(true)}
                                    disabled={projectLabelsLoading}
                                />
                                {labelDropdownOpen && filteredLabels.length > 0 && (
                                    <div
                                        style={{
                                            position: "absolute",
                                            zIndex: 1050,
                                            top: "100%",
                                            left: 0,
                                            right: 0,
                                            maxHeight: "200px",
                                            overflowY: "auto",
                                            border: "1px solid #ced4da",
                                            borderRadius: "0 0 4px 4px",
                                            backgroundColor: "#fff",
                                            boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                                        }}
                                    >
                                        {filteredLabels.map((label) => {
                                            const isSelected = selectedLabels.includes(label.name);
                                            return (
                                                <div
                                                    key={label.id}
                                                    onClick={() => toggleLabel(label.name)}
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        padding: "6px 10px",
                                                        cursor: "pointer",
                                                        backgroundColor: isSelected ? "#f0f0f0" : "#fff",
                                                    }}
                                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = isSelected ? "#e8e8e8" : "#f8f8f8")}
                                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = isSelected ? "#f0f0f0" : "#fff")}
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={isSelected}
                                                        readOnly
                                                        style={{ marginRight: "8px", cursor: "pointer" }}
                                                    />
                                                    <span
                                                        style={{
                                                            display: "inline-block",
                                                            width: "12px",
                                                            height: "12px",
                                                            borderRadius: "50%",
                                                            backgroundColor: label.color || "#e0e0e0",
                                                            marginRight: "8px",
                                                            flexShrink: 0,
                                                        }}
                                                    />
                                                    <span style={{ fontSize: "13px" }}>{label.name}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <FormText color="muted">
                                &quot;Bug&quot; und &quot;Status::Backlog&quot; werden automatisch hinzugef{"\u00FC"}gt.
                            </FormText>
                        </FormGroup>
                        <FormGroup style={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-end" }}>
                            <LoadingSpinnerButton loading={!!bugReportLoading} type="submit" disabled={!bugTitle.trim()}>
                                Bug melden
                            </LoadingSpinnerButton>
                            <Button color="secondary" disabled={bugReportLoading} onClick={handleClose}>
                                Abbrechen
                            </Button>
                        </FormGroup>
                    </Form>
                )}

                {bugReportResult && (
                    <div>
                        <AlertComponent
                            successMessages={`GitLab Issue #${bugReportResult.issueIid} wurde erstellt. Customer Value: ${bugReportResult.customerValue}`}
                        />
                        <div style={{ marginBottom: "1rem" }}>
                            <Button
                                color="primary"
                                tag="a"
                                href={bugReportResult.issueUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <i className="fa fa-external-link" style={{ marginRight: "0.5rem" }} />
                                Issue #{bugReportResult.issueIid} in GitLab {"\u00F6"}ffnen
                            </Button>
                        </div>
                        <Button color="secondary" onClick={handleClose}>
                            Schlie{"\u00DF"}en
                        </Button>
                    </div>
                )}

                {bugReportError && !bugReportResult && (
                    <AlertComponent errorMessages={bugReportError} />
                )}
            </ModalBody>
        </Modal>
    );
};
